import React, { useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  makeStyles,
  IconButton,
  Typography,
  Box,
  Grid
} from '@material-ui/core'
import { Close as CloseIcon, Info as InfoIcon } from '@material-ui/icons'
import { Form, Formik } from 'formik'
import CustomTextField from 'components/Form/CustomTextField'
import ProgressButton from 'components/ProgressButton'
import { useSelector } from 'react-redux'
import { createAndInviteSpeaker, updateAndReinviteSpeaker } from 'store/speaker'
import { colors } from 'utils/colors'
import InfoIconAlongText from 'components/InfoIconAlongText'
import { SPEAKER_INFO_TEXT, SpeakerRoles } from 'constants/index'
import CustomCheckboxGroup from 'components/Form/CustomCheckboxGroup'
import { inviteSpeakerFormSchema } from 'schemas/speaker'

const speakerRoleOptions = [
  { value: SpeakerRoles.PRESENTER, label: 'Presenter' },
  { value: SpeakerRoles.MODERATOR, label: 'Moderator' }
]

const InviteSpeakerModal = ({ open, onClose, editMode = false, speakerData = null }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const { user } = useSelector((state) => state.auth)

  const initialValues = {
    firstName: speakerData?.firstName || '',
    lastName: speakerData?.lastName || '',
    email: speakerData?.email || '',
    roles: speakerData?.roles || []
  }

  const handleSubmit = async (values) => {
    setLoading(true)
    try {
      if (editMode && speakerData) {
        const emailChanged = values.email !== speakerData?.email
        const res = await updateAndReinviteSpeaker(
          values,
          user?.conferenceId,
          speakerData.id,
          emailChanged
        )
        res && onClose()
      } else {
        await createAndInviteSpeaker(values, user?.conferenceId)
        onClose()
      }
    } catch (_error) {
      // error handled in above function for displaying toast
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        {editMode ? 'Edit Speaker Invitation' : 'Invite Speaker'}
        <IconButton className={classes.closeButton} onClick={onClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={inviteSpeakerFormSchema}
        onSubmit={handleSubmit}
        enableReinitialize>
        {({ isValid, values }) => (
          <Form>
            <DialogContent className={classes.dialogContent}>
              <Box className={classes.infoBox}>
                <InfoIcon className={classes.infoIcon} />
                <Typography variant="body2">
                  {editMode && values.email !== speakerData?.email ? (
                    <>
                      You&apos;re changing the speaker&apos;s email address. A new invitation link
                      will be generated and sent to the new email address.
                    </>
                  ) : editMode ? (
                    <>
                      Update the speaker&apos;s information. If you don&apos;t change the email
                      address, the existing invitation link will remain valid.
                    </>
                  ) : (
                    <>
                      An invitation email will be sent to the speaker. They will receive a link to
                      complete their profile information and additional details for the conference.
                    </>
                  )}
                </Typography>
              </Box>

              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Speaker Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                  <CustomTextField
                    label="First Name"
                    name="firstName"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <CustomTextField
                    label="Last Name"
                    name="lastName"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Email Address"
                    name="email"
                    type="email"
                    placeholder="Email address"
                    fullWidth
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} lg={6}>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                    <label className={classes.label}>
                      Roles
                      <InfoIconAlongText infoText={SPEAKER_INFO_TEXT.roles} />
                    </label>
                    <Box style={{ marginLeft: '4px' }}>
                      <CustomCheckboxGroup
                        name="roles"
                        options={speakerRoleOptions}
                        labelField="label"
                        valueField="value"
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button onClick={onClose} disabled={loading} className={classes.cancelButton}>
                Cancel
              </Button>
              <ProgressButton
                type="submit"
                label={
                  editMode && values.email !== speakerData?.email
                    ? 'Update & Invite'
                    : editMode
                    ? 'Update'
                    : 'Create & Invite'
                }
                inProgress={loading}
                disabled={loading || !isValid}
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default InviteSpeakerModal

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& h2': {
      fontWeight: 600,
      fontSize: '20px'
    }
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  cancelButton: {
    marginRight: theme.spacing(1)
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  infoIcon: {
    color: theme.palette.info.main,
    fontSize: 20,
    marginTop: '2px'
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    color: '#2c3e50',
    fontSize: '16px'
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.indigo
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.indigo,
        borderWidth: '2px'
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: colors.indigo
    }
  },
  label: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.2rem'
  }
}))
